import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TelegramIcon from '@mui/icons-material/Telegram';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SvgIcon from '@mui/material/SvgIcon';
import { isUserLoggedIn } from '../utils/authSession';

function SnapchatIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12.206 1c.063 0 .127.002.191.004 1.707.043 3.065.723 4.037 2.022.72.963 1.063 2.088 1.063 3.244 0 .457-.04.91-.118 1.352-.044.244-.094.487-.15.726l-.017.076c-.02.085-.028.14-.028.183 0 .155.07.235.317.343.08.035.168.065.284.1l.072.022c.63.196 1.128.429 1.478.77.243.237.38.527.38.852 0 .64-.62 1.083-1.875 1.345-.152.031-.354.062-.59.094a3.786 3.786 0 0 0-.384.068c-.19.047-.284.124-.332.276a1.224 1.224 0 0 0-.028.337c.01.105.029.212.057.319.215.825.647 1.6 1.283 2.3.372.41.796.77 1.262 1.074.425.277.895.494 1.39.635.29.082.486.207.535.405a.584.584 0 0 1-.04.372c-.327.705-1.397 1.104-2.18 1.284-.066.015-.12.073-.138.145l-.04.176c-.044.184-.09.375-.197.506-.117.143-.305.208-.624.218a5.55 5.55 0 0 1-.545-.038 5.632 5.632 0 0 0-.71-.05c-.204 0-.41.02-.614.058a4.373 4.373 0 0 0-.697.226c-.57.243-1.076.564-1.72.564h-.055c-.645 0-1.15-.321-1.72-.564a4.373 4.373 0 0 0-.697-.226 3.637 3.637 0 0 0-.614-.059c-.252 0-.484.02-.71.051a5.55 5.55 0 0 1-.545.038c-.32-.01-.507-.075-.624-.218-.107-.131-.153-.322-.198-.506l-.039-.176c-.018-.072-.072-.13-.138-.145-.783-.18-1.853-.58-2.18-1.284a.584.584 0 0 1-.04-.372c.05-.198.245-.323.535-.405a5.697 5.697 0 0 0 1.39-.635 5.893 5.893 0 0 0 1.262-1.073c.636-.7 1.068-1.476 1.283-2.301a1.55 1.55 0 0 0 .057-.319 1.17 1.17 0 0 0-.028-.337c-.048-.152-.142-.229-.332-.276a3.786 3.786 0 0 0-.384-.068 7.198 7.198 0 0 1-.59-.094C3.62 12.017 3 11.574 3 10.934c0-.325.137-.615.38-.852.35-.341.847-.574 1.478-.77l.072-.022c.116-.035.204-.065.284-.1.247-.108.317-.188.317-.343 0-.043-.008-.098-.028-.183l-.017-.076a11.664 11.664 0 0 1-.15-.726 8.084 8.084 0 0 1-.118-1.352c0-1.156.344-2.281 1.063-3.244C7.253 2.867 8.27 2.14 9.63 1.77A5.255 5.255 0 0 1 12.206 1z" />
    </SvgIcon>
  );
}

const SOCIAL_LINKS = [
  { key: 'facebook', label: 'Facebook', url: 'https://www.facebook.com/share/18erhSDE58/', icon: FacebookIcon, color: '#1877F2' },
  { key: 'instagram', label: 'Instagram', url: 'https://www.instagram.com/bardawamcrysrtal?igsh=MXI2MW9vd3kxZnU2MA==', icon: InstagramIcon, color: '#E4405F' },
  { key: 'youtube', label: 'YouTube', url: 'https://youtube.com/@dr.bardawam?si=_eFE52crmQ3mLlpv', icon: YouTubeIcon, color: '#FF0000' },
  { key: 'tiktok', label: 'TikTok', url: 'https://www.tiktok.com/@bardawamcrystal', icon: MusicNoteIcon, color: '#010101' },
  { key: 'snapchat', label: 'Snapchat', url: 'https://www.snapchat.com/add/drbardawam?share_id=F4U_3yzQ04k&locale=en-GB', icon: SnapchatIcon, color: '#FFFC00' },
  { key: 'telegram', label: 'Telegram', url: 'https://t.me/drbardawam', icon: TelegramIcon, color: '#0088cc' },
];

const QUICK_LINKS = [
  { labelKey: 'categories', path: '/' },
  { labelKey: 'search', path: '/search' },
  { labelKey: 'cart_title', path: '/cart' },
  { labelKey: 'delivery_page', path: '/delivery' },
  { labelKey: 'offers_page', path: '/offers' },
  { labelKey: 'download_app_page', path: '/download' },
];

const FAVOURITES_LINK = { labelKey: 'Favourites', path: '/favourites' };
const ORDERS_LINK = { labelKey: 'orders.header', path: '/orders' };
const PROFILE_LINK = { labelKey: 'Profile', path: '/profile' };

const AUTH_LINKS = [
  { labelKey: 'Login', path: '/signin' },
  { labelKey: 'Register', path: '/register' },
];

function SectionTitle({ children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
        {children}
      </Typography>
      <Box sx={{ width: 36, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.55)' }} />
    </Box>
  );
}

function FooterLink({ to, children }) {
  return (
    <Typography
      component={RouterLink}
      to={to}
      variant="body2"
      sx={{
        color: 'rgba(255,255,255,0.88)',
        textDecoration: 'none',
        py: 0.5,
        display: 'block',
        fontWeight: 600,
        fontSize: { xs: 14, md: 15.5 },
        transition: 'all 0.2s ease',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        userSelect: 'none',
        '&:hover': {
          color: '#fff',
          paddingInlineStart: 0.5,
        },
      }}
    >
      {children}
    </Typography>
  );
}

function SocialButtons({ variant = 'compact' }) {
  if (variant === 'grid') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
        }}
      >
        {SOCIAL_LINKS.map(({ key, label, url, icon: Icon, color }) => (
          <Box
            key={key}
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.75,
              textDecoration: 'none',
              color: '#fff',
              p: 1.5,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              transition: 'all 0.25s ease',
              '&:hover': {
                bgcolor: color,
                borderColor: color,
                transform: 'translateY(-3px)',
                boxShadow: `0 8px 24px rgba(0,0,0,0.22)`,
              },
            }}
          >
            <Icon sx={{ fontSize: 26 }} />
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, opacity: 0.92 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
      {SOCIAL_LINKS.map(({ key, label, url, icon: Icon, color }) => (
        <IconButton
          key={key}
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          sx={{
            bgcolor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.22)',
            width: 42,
            height: 42,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: color,
              borderColor: color,
              transform: 'translateY(-3px)',
              boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
            },
          }}
        >
          <Icon fontSize="small" />
        </IconButton>
      ))}
    </Stack>
  );
}

function DesktopFooter({ t, accountLinks }) {
  return (
    <Grid container spacing={{ md: 3, lg: 5 }} alignItems="flex-start">
      <Grid size={{ md: 12, lg: 5 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
      
          <Box>
            <Typography variant="h5" fontWeight={800} lineHeight={1.2}>
              {t('app_name')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
              {t('footer_tagline')}
            </Typography>
          </Box>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            opacity: 0.88,
            lineHeight: 1.9,
            maxWidth: 420,
            pl: 0.5,
          }}
        >
          {t('register_subtitle')}
        </Typography>
      </Grid>

      <Grid size={{ md: 4, lg: 2.5 }}>
        <SectionTitle>{t('footer_quick_links')}</SectionTitle>
        <Stack spacing={0.25}>
          {QUICK_LINKS.map((link) => (
            <FooterLink key={link.path} to={link.path}>
              {t(link.labelKey)}
            </FooterLink>
          ))}
        </Stack>
      </Grid>

      <Grid size={{ md: 4, lg: 2.5 }}>
        <SectionTitle>{t('Account')}</SectionTitle>
        <Stack spacing={0.25}>
          {accountLinks.map((link) => (
            <FooterLink key={link.path} to={link.path}>
              {t(link.labelKey)}
            </FooterLink>
          ))}
        </Stack>
      </Grid>

      <Grid size={{ md: 4, lg: 2 }}>
        <SectionTitle>{t('social_media')}</SectionTitle>
        <SocialButtons variant="grid" />
      </Grid>
    </Grid>
  );
}

function MobileFooter({ t, accountLinks }) {
  return (
    <Stack spacing={3} alignItems="center" textAlign="center">
      <Stack direction="row" alignItems="center" spacing={1.25}>
       
        <Typography variant="h6" fontWeight={800}>
          {t('app_name')}
        </Typography>
      </Stack>

      <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.8, maxWidth: 320 }}>
        {t('footer_tagline')}
      </Typography>

      <SocialButtons />

      <Grid container spacing={1} sx={{ width: '100%', maxWidth: 360 }}>
        {[...QUICK_LINKS, ...accountLinks].map((link) => (
          <Grid key={link.path} size={{ xs: 6 }}>
            <FooterLink to={link.path}>{t(link.labelKey)}</FooterLink>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const year = new Date().getFullYear();
  const [loggedIn, setLoggedIn] = useState(false);

  const refreshAuth = useCallback(async () => {
    setLoggedIn(await isUserLoggedIn());
  }, []);

  useEffect(() => {
    refreshAuth();
    const onProfileUpdated = () => refreshAuth();
    window.addEventListener('profile-updated', onProfileUpdated);
    return () => window.removeEventListener('profile-updated', onProfileUpdated);
  }, [refreshAuth]);

  const accountLinks = loggedIn
    ? [FAVOURITES_LINK, ORDERS_LINK, PROFILE_LINK]
    : [FAVOURITES_LINK, ...AUTH_LINKS];

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        color: '#fff',
        background: 'linear-gradient(165deg, #6B4F28 0%, #B8894A 42%, #D4A574 100%)',
        pt: { xs: 4, md: 7 },
        pb: { xs: 3, md: 4 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.45), rgba(255,255,255,0.1))',
        },
      }}
    >
      <Container maxWidth="lg">
        {isMobile ? <MobileFooter t={t} accountLinks={accountLinks} /> : <DesktopFooter t={t} accountLinks={accountLinks} />}

        <Divider sx={{ my: { xs: 2.5, md: 4 }, borderColor: 'rgba(255,255,255,0.22)' }} />

        <Typography variant="body2" align="center" sx={{ opacity: 0.88, fontWeight: 600, lineHeight: 1.8 }}>
          © {year} {t('app_name')}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{ opacity: 0.75, fontWeight: 600, mt: 0.75, fontSize: '0.82rem' }}
        >
          {t('created_by')}{' '}
          <Box
            component="a"
            href="https://www.facebook.com/techtopiaa01/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#fff',
              fontWeight: 800,
              textDecoration: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.4)',
              transition: 'all 0.2s ease',
              '&:hover': {
                opacity: 1,
                borderBottomColor: '#fff',
              },
            }}
          >
            Techtopia
          </Box>
        </Typography>
      </Container>
    </Box>
  );
}
